import { PDFDocument, degrees, rgb } from "pdf-lib";
import fontkit from "@pdf-lib/fontkit";
import { annotationToPdfPlacement, formatDate, textBaselineToPdf } from "./pdf-core.js";

export async function exportAnnotatedPdf(originalBytes, annotations, fontBytes, { formValues = {}, flattenForm = false } = {}) {
  const documentCopy = await PDFDocument.load(originalBytes.slice ? originalBytes.slice() : originalBytes);
  applyFormValues(documentCopy, formValues, flattenForm);
  documentCopy.registerFontkit(fontkit);
  const [latinFont, georgianFont] = await Promise.all([
    documentCopy.embedFont(fontBytes.latin, { subset: true }),
    documentCopy.embedFont(fontBytes.georgian, { subset: true })
  ]);
  const imageCache = new Map();
  const pages = documentCopy.getPages();

  for (const annotation of annotations) {
    const page = pages[annotation.page - 1];
    if (!page) continue;
    const cropBox = page.getCropBox();
    const rotation = page.getRotation().angle;
    if (annotation.type === "signature" || annotation.type === "initials") {
      let image = imageCache.get(annotation.image);
      if (!image) {
        const imageBytes = dataUrlBytes(annotation.image);
        image = await documentCopy.embedPng(imageBytes);
        imageCache.set(annotation.image, image);
      }
      const placement = annotationToPdfPlacement(annotation, cropBox, rotation);
      page.drawImage(image, {
        x: placement.x,
        y: placement.y,
        width: placement.width,
        height: placement.height,
        rotate: degrees(placement.rotation)
      });
    } else {
      const text = annotation.type === "date" ? formatDate(annotation.value, annotation.dateFormat) : annotation.value;
      if (!text) continue;
      const font = /[\u10A0-\u10FF\u2D00-\u2D2F]/u.test(text) ? georgianFont : latinFont;
      const placement = annotationToPdfPlacement(annotation, cropBox, rotation);
      let fontSize = annotation.fontSize;
      const measured = font.widthOfTextAtSize(text, fontSize);
      const available = Math.max(5, placement.width - 4);
      if (measured > available) fontSize = Math.max(5, fontSize * available / measured);
      const baseline = textBaselineToPdf({ ...annotation, fontSize }, cropBox, rotation);
      page.drawText(text, {
        x: baseline.x,
        y: baseline.y,
        size: fontSize,
        font,
        color: rgb(0.09, 0.13, 0.2),
        rotate: degrees(placement.rotation)
      });
    }
  }

  return documentCopy.save({ useObjectStreams: true });
}

export function applyFormValues(document, values, flatten = false) {
  const entries = values instanceof Map ? [...values.entries()] : Object.entries(values || {});
  if (!entries.length && !flatten) return;
  const form = document.getForm();
  for (const [name, change] of entries) {
    const field = form.getFieldMaybe(name);
    if (!field) continue;
    try {
      if (change.type === "text" && typeof field.setText === "function") field.setText(String(change.value || ""));
      else if (change.type === "checkbox" && typeof field.check === "function") change.value ? field.check() : field.uncheck();
      else if (change.type === "radio" && typeof field.select === "function" && change.value) field.select(String(change.value));
      else if (["choice", "dropdown"].includes(change.type) && typeof field.select === "function") field.select(String(change.value || ""));
    } catch { /* Leave malformed or unsupported fields unchanged. */ }
  }
  if (flatten) form.flatten({ updateFieldAppearances: true });
}

function dataUrlBytes(dataUrl) {
  const base64 = dataUrl.split(",")[1];
  if (!base64) throw new Error("The signature image is invalid.");
  if (typeof Buffer !== "undefined") return Uint8Array.from(Buffer.from(base64, "base64"));
  const binary = atob(base64);
  return Uint8Array.from(binary, character => character.charCodeAt(0));
}
